import { useEffect, useState } from "react";
import { Lock, TimerReset } from "lucide-react";

// Renders a live countdown to `cutoffIso` (UTC ISO string).
// Returns visually:
//   > 30 min  → blue banner
//   ≤ 30 min  → amber
//   ≤ 10 min  → red flashing
//   past      → grey "PICKS LOCKED"
export default function CountdownBanner({ cutoffIso, meetingDate }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!cutoffIso) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [cutoffIso]);

  if (!cutoffIso) {
    return (
      <div
        className="rounded-2xl p-4 border font-bold text-sm bg-[#f8f9fa] border-[#e9ecef] text-[#6c757d]"
        data-testid="countdown-banner-no-races"
      >
        No races loaded for this date yet.
      </div>
    );
  }

  const diffMs = new Date(cutoffIso).getTime() - now;
  const locked = diffMs <= 0;
  const totalSecs = Math.max(0, Math.floor(diffMs / 1000));
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  const pad = (n) => String(n).padStart(2, "0");

  let bg, border, text, icon, label, testState;
  if (locked) {
    bg = "bg-[#e9ecef]"; border = "border-[#adb5bd]"; text = "text-[#495057]";
    icon = <Lock className="w-5 h-5" strokeWidth={2.5} />;
    label = "PICKS LOCKED";
    testState = "locked";
  } else if (diffMs <= 10 * 60 * 1000) {
    bg = "bg-[#fdecec] animate-pulse"; border = "border-[#c92a2a]/40"; text = "text-[#c92a2a]";
    icon = <TimerReset className="w-5 h-5" strokeWidth={2.5} />;
    label = "Picks close in";
    testState = "critical";
  } else if (diffMs <= 30 * 60 * 1000) {
    bg = "bg-[#fff4e6]"; border = "border-[#c07800]/40"; text = "text-[#c07800]";
    icon = <TimerReset className="w-5 h-5" strokeWidth={2.5} />;
    label = "Picks close in";
    testState = "warning";
  } else {
    bg = "bg-[#e7f0fb]"; border = "border-[#1971c2]/30"; text = "text-[#1971c2]";
    icon = <TimerReset className="w-5 h-5" strokeWidth={2.5} />;
    label = "Picks close in";
    testState = "normal";
  }

  return (
    <div
      className={`rounded-2xl p-4 border font-bold ${bg} ${border} ${text} flex items-center gap-3`}
      data-testid="countdown-banner"
      data-state={testState}
    >
      {icon}
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wider font-black opacity-80">
          {meetingDate ? `Meeting ${meetingDate}` : "Meeting"}
        </div>
        <div className="font-display text-lg leading-tight">
          {locked ? label : (
            <>
              {label}{" "}
              <span data-testid="countdown-timer" className="tabular-nums">
                {h > 0 ? `${h}h ` : ""}{pad(m)}m {pad(s)}s
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
